import { useRef, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { ErrorMessage } from 'formik';
import { useButton, useComboBox, useFilter } from 'react-aria';
import { useComboBoxState } from 'react-stately';
import type { ComboBoxProps } from '@react-types/combobox';
import type { CollectionChildren } from '@react-types/shared';
import ListBox from './ListBox';
import Popover from './Popover/Popover';
import LoadingSpinner from '../../LoadingSpinner';
import { ArrowLeft2 } from '../../../assets/icons';
import s from './UncontrolledComboBox.module.scss';
import InputErrorMessage from '../shared/InputErrorMessage';
import { fieldErrorElementId, joinClassNames } from '../shared/formFieldUi';

export interface UncontrolledComboBoxItem {
  id: string;
  name: string;
}

type UncontrolledComboBoxProps<T extends UncontrolledComboBoxItem> = Omit<
  ComboBoxProps<T>,
  'children'
> & {
  name: string;
  label: ReactNode;
  handleLoadMore: () => void;
  isLoading?: boolean;
  isError?: boolean;
  isRequired?: boolean;
  children: CollectionChildren<T>;
};

/**
 * A ComboBox based on react-aria from Adobe
 * Taken from https://react-spectrum.adobe.com/react-aria/useComboBox.html
 */
function UncontrolledComboBox<T extends UncontrolledComboBoxItem>(
  props: UncontrolledComboBoxProps<T>
) {
  const {
    label,
    handleLoadMore,
    isLoading = false,
    isError = false,
    isRequired = false,
    name,
    children,
    ...comboBoxStateProps
  } = props;

  const errorMessageId = fieldErrorElementId(name);

  const { contains } = useFilter({ sensitivity: 'base' });
  const state = useComboBoxState({
    ...comboBoxStateProps,
    children,
    defaultFilter: contains,
    allowsEmptyCollection: true,
  });

  const buttonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listBoxRef = useRef<HTMLUListElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const {
    buttonProps: triggerProps,
    inputProps,
    listBoxProps,
    labelProps,
  } = useComboBox(
    {
      ...comboBoxStateProps,
      label,
      isRequired,
      inputRef,
      buttonRef,
      listBoxRef,
      popoverRef,
      id: name,
      'aria-describedby': errorMessageId,
    },
    state
  );

  const { buttonProps } = useButton(triggerProps, buttonRef);

  const arrowClasses = joinClassNames(s.arrow, state.isOpen && s.flippedArrow);

  let additionalPopOverContent: ReactNode;
  if (isLoading) {
    additionalPopOverContent = <LoadingSpinner color="grey" />;
  } else if (state.collection.size === 0) {
    additionalPopOverContent = (
      <div>
        <FormattedMessage id="combobox.noResults" />
      </div>
    );
  }

  return (
    <div className={s.outerWrapper}>
      {/* oxlint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label {...labelProps} className={isError ? s.errorLabel : ''}>
        {label}
        {isRequired && <span>*</span>}
      </label>
      <div className={s.popOverAnchor}>
        <div className={`${s.innerWrapper} ${isError ? s.errorBorder : ''}`}>
          <input
            /* oxlint-disable react/jsx-props-no-spreading */
            {...inputProps}
            ref={inputRef}
            className={s.inputElement}
          />
          {/* oxlint-disable-next-line react/button-has-type */}
          <button {...buttonProps} ref={buttonRef} className={s.button}>
            <div aria-hidden="true" className={arrowClasses}>
              <ArrowLeft2 />
            </div>
          </button>
        </div>
        {state.isOpen && (
          <Popover
            popoverRef={popoverRef}
            isOpen={state.isOpen}
            onClose={() => {
              state.close();
            }}
          >
            <div
              role="region"
              aria-live="polite"
              className={
                additionalPopOverContent ? s.additionalPopOverContent : ''
              }
            >
              {additionalPopOverContent}
            </div>
            <ListBox
              {...listBoxProps}
              listBoxRef={listBoxRef}
              state={state}
              handleLoadMore={handleLoadMore}
            />
          </Popover>
        )}
      </div>
      <ErrorMessage
        id={errorMessageId}
        name={name}
        component={InputErrorMessage}
      />
    </div>
  );
}

export default UncontrolledComboBox;
